/**
 * The palate profile.
 *
 * The comparison is the whole point — an average score in isolation says nothing.
 * "You rate Cabernet 0.8 above the room" is a fact about *you*, and it's been
 * sitting in the scores table unread for forty years.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { palateApi, type PalateFacet } from '../api/palate';
import { Panel, StatTile } from './Widgets';

const signed = (n: number) => `${n > 0 ? '+' : ''}${n.toFixed(2)}`;

/** How the member scores this category, relative to the club. */
function FacetRow({ facet }: { facet: PalateFacet }) {
  // Scale the bar against a ±1.5 point swing — beyond that is a strong opinion.
  const pct = Math.min(Math.abs(facet.delta) / 1.5, 1) * 50;
  const above = facet.delta >= 0;

  return (
    <li className="flex items-center gap-3 py-1.5">
      <span className="w-28 shrink-0 truncate text-sm text-cream-200" title={facet.label}>
        {facet.label}
      </span>

      {/* A centre line: right of it you're more generous than the club, left less. */}
      <span className="relative h-2 flex-1 rounded-full bg-ink-800">
        <span className="absolute left-1/2 top-1/2 h-3 w-px -translate-y-1/2 bg-cream-300/25" />
        <span
          className={`absolute top-0 h-2 rounded-full ${above ? 'bg-gold-500' : 'bg-wine-500'}`}
          style={
            above
              ? { left: '50%', width: `${pct}%` }
              : { right: '50%', width: `${pct}%` }
          }
        />
      </span>

      <span
        className={`w-14 shrink-0 text-right text-xs tabular-nums ${
          above ? 'text-gold-400' : 'text-wine-300'
        }`}
        title={`You ${facet.average.toFixed(2)} · club ${facet.groupAverage.toFixed(2)}`}
      >
        {signed(facet.delta)}
      </span>
      <span className="w-8 shrink-0 text-right text-xs text-cream-300/40">×{facet.count}</span>
    </li>
  );
}

function Facets({ title, facets }: { title: string; facets: PalateFacet[] }) {
  if (!facets.length) return null;
  return (
    <Panel title={title}>
      <ul className="divide-y divide-ink-800/60">
        {facets.map((f) => (
          <FacetRow key={f.label} facet={f} />
        ))}
      </ul>
    </Panel>
  );
}

export function PalatePanel({ memberId, name }: { memberId: string; name: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['palate', memberId],
    queryFn: () => palateApi.get(memberId),
  });

  if (isLoading) {
    return <div className="card p-6 text-sm text-cream-300/60">Reading the palate…</div>;
  }
  if (!data || data.scoresCast === 0) {
    return (
      <div className="card p-8 text-center text-sm text-cream-300/50">
        No scores yet — a palate takes a few tastings to emerge.
      </div>
    );
  }

  const generous = data.generosity >= 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Scores cast" value={String(data.scoresCast)} />
        <StatTile label="Tastings" value={String(data.eventsScored)} />
        <StatTile label="Your average" value={data.overallAverage.toFixed(2)} />
        <StatTile
          label={generous ? 'More generous than the club' : 'Harder marker than the club'}
          value={signed(data.generosity)}
        />
      </div>

      <p className="text-sm leading-relaxed text-cream-300/70">
        Across {data.scoresCast} scores, {name.split(' ')[0]} averages{' '}
        <strong className="text-cream-100">{data.overallAverage.toFixed(2)}</strong> against the
        club's {data.groupOverallAverage.toFixed(2)} — {generous ? 'a touch more generous' : 'a slightly harder marker'} than
        the room. The bars below show where that opinion is strongest: right of the line is above
        the club average, left is below.
      </p>

      <div className="grid gap-4 md:grid-cols-2">
        <Facets title="By grape" facets={data.byVarietal} />
        <Facets title="By region" facets={data.byRegion} />
        <Facets title="By colour" facets={data.byColor} />
        <Facets title="By decade" facets={data.byDecade} />
      </div>

      {data.topWines.length ? (
        <Panel title="Their highest scores">
          <ul className="divide-y divide-ink-800/60">
            {data.topWines.map((w) => (
              <li key={`${w.id}-${w.total}`} className="flex items-baseline justify-between py-2">
                <Link to={`/cellar/${w.id}`} className="min-w-0 hover:text-gold-300">
                  <span className="block truncate text-sm text-cream-100">{w.name}</span>
                  <span className="block truncate text-xs text-cream-300/50">
                    {[w.producer, w.vintage].filter(Boolean).join(' · ')}
                  </span>
                </Link>
                <span className="shrink-0 font-display text-lg text-gold-200">{w.total}</span>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </div>
  );
}
